import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SuiteSummary } from '../api.ts';
import styles from './SuiteSwitcher.module.css';

interface SuiteSwitcherProps {
  suites: SuiteSummary[];
  current: SuiteSummary;
  onSelect: (suiteId: string) => void;
  disabled?: boolean;
}

/**
 * Renders the active QC suite as a heading. When more than one suite exists, a
 * subtle chevron opens a menu to switch. The suite is meant to feel like fixed
 * context (eventually auto-detected), not an up-front decision — so switching is
 * tucked behind the chevron rather than presented as a form control.
 */
export function SuiteSwitcher({ suites, current, onSelect, disabled }: SuiteSwitcherProps) {
  const { t } = useTranslation('ui');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Single suite: nothing to switch to — just show the title.
  if (suites.length <= 1) {
    return (
      <div className={styles.container}>
        <span className={styles.programLabel}>{t('program.label')}</span>
        <h2 className={styles.title}>{current.displayName}</h2>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <span className={styles.programLabel}>{t('program.label')}</span>
      <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <h2 className={styles.title}>{current.displayName}</h2>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden="true">▾</span>
      </button>

      {open && (
        <ul className={styles.menu} role="listbox" aria-label={t('program.selectAria')}>
          {suites.map((s) => {
            const active = s.suiteId === current.suiteId;
            return (
              <li key={s.suiteId} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`${styles.option} ${active ? styles.optionActive : ''}`}
                  onClick={() => { onSelect(s.suiteId); setOpen(false); }}
                >
                  <span>{s.displayName}</span>
                  {active && <span className={styles.check} aria-hidden="true">✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      </div>
    </div>
  );
}
